import BaseService            from '@/core/services/BaseService';
import Core                   from '@/core/Core';
import Unit                   from '@/domains/Units/Unit';
import UnitsDAO               from '@/domains/Units/UnitsDAO';
import Dictionary             from '@/core/types/Dictionary';
import ContextMenuItem        from '@/lib/components/VContextMenu/ContextMenuItem';
import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';


class UnitsService extends BaseService {

  /**
   * Кеш единиц измерения. Наполняется из Dictionaries общим эндпоинтом.
   * reactive — чтобы потребители перерисовывались, когда словарь долетит после логина.
   */
  static units = reactive(new Dictionary<number, Unit>());

  static async get(id: number): Promise<Unit | null> {
    const response = await UnitsDAO.get(id);
    return Core.utils.hydrate(response.data, Unit.fromServer);
  }

  static async getListPaginated(
    params?: Parameters<typeof UnitsDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
  ): Promise<{ data: Unit[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await UnitsDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Unit.fromServer),
      pagination: response.meta.pagination,
    };
  }

  static async getList(params?: Parameters<typeof UnitsDAO.getList>[0]): Promise<Unit[]> {
    return (await this.getListPaginated(params)).data;
  }

  static async add(unit: Unit): Promise<boolean> {
    const response = await UnitsDAO.add(unit);

    if (response.ok) {
      Core.showNotification.success('Единица измерения создана');

      const created = await this.get(response.data);
      if (Core.utils.isNullOrUndefined(created) === false) {
        this.units.add(created);
      }
    }

    return response.ok;
  }

  static async update(unit: Unit): Promise<boolean> {
    const response = await UnitsDAO.update(unit);

    if (response.ok) {
      Core.showNotification.success('Единица измерения обновлена');

      const updated = await this.get(unit.id);
      if (Core.utils.isNullOrUndefined(updated) === false) {
        this.units.add(updated);
      }
    }

    return response.ok;
  }

  static async delete(id: number): Promise<boolean> {
    const response = await UnitsDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Единица измерения удалена');
      this.units.delete(id);
    }

    return response.ok;
  }

  /**
   * Удаление с подтверждением. Используется из контекстного меню единицы измерения.
   * Старый delete(id) оставлен для массовых операций без UX.
   */
  static async deleteWithConfirmation(unit: Unit): Promise<boolean> {
    const confirmed = await Core.dialogs.confirm(
      'Удалить эту единицу измерения?',
      `${unit.nameFull} / ${unit.name}`,
    );
    if (confirmed === true) {
      return await this.delete(unit.id);
    }
    else {
      return false;
    }
  }

  /**
   * Открыть карточку единицы измерения.
   * Промис резолвится по закрытию карточки: true — сохранили, false — закрыли.
   */
  static showUnitCard(unit: Unit | null): Promise<boolean> {
    return Core.dialogs.UnitCard.showAsPromise({ id: unit?.id });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Пункты контекстного меню единицы измерения для грида реестра и карточки.
   * Грид обновляет список, а карточка закрывается через resolve(), только если
   * callback пункта вернул true.
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (unit: Unit) => { return this.showUnitCard(unit); },
        null,
        false,  // hidden
        true,   // dividerAfter
      ),
      new ContextMenuItem(
        'История действий',
        'history',
        (unit: Unit) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'unit', entityId: unit.id, showAddComment: false }); },
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (unit: Unit) => { return this.deleteWithConfirmation(unit); },
      ),
    ];
  }
}

export default UnitsService;